import { kitConfig, resolveLink, safeReturn } from "./config.js";
import { ApiError, apiKey, call, connectionMessageKeys, connectionProblem, idOfPublicId, isApiError, mediaUrl, publicIdOf, useApiQuery } from "./api.js";
import { formatDateTime, formatRelative } from "./format.js";
import { localeNames, useT } from "./i18n.js";
import { claimDevice, clearDeviceCopies, clearDeviceState, clearUserScopedState, pendingWorkCount, useAccountLocaleSync, useIdentityChangeReload, useLocaleSwitching, useSession, useSignOut, useSwitchLocale } from "./session.js";
import { t as ThemeToggle } from "./theme-toggle-gP7w4XRa.js";
import { Ct as cn, l as Button$1, pt as FieldSelect, rt as useToast, xt as StateView, yt as SkeletonList } from "./ui-1DW1tZfU.js";
import { appLinks, appUrl, dropOrganizationSegment, expandPath, isPublicId, isUuid, locales, shortenPath } from "@hlt/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, redirect, useMatches, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { ActionIcon, Alert, Anchor, AppShell, Avatar, Breadcrumbs as Breadcrumbs$1, Button, Drawer, Indicator, Loader, Menu, NavLink, Popover, ScrollArea, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { AlertTriangle, Bell, Check, ChevronRight, ChevronsUpDown, CircleUser, ClipboardCheck, FileDown, Gauge, Languages, LayoutGrid, ListChecks, LogOut, Mail, Megaphone, PanelLeftClose, PanelLeftOpen, Settings, Share2, Undo2 } from "lucide-react";
// src/shell/app-anchor.tsx
/**
* Link to a place in any HLT app: a client-side route inside this app, a full page load into another
* app (same account, the session is shared across app origins).
*/
function AppAnchor({ link, children, ...rest }) {
	const resolved = resolveLink(link);
	if (!resolved) return /* @__PURE__ */ jsx("span", {
		className: rest.className,
		children
	});
	if (resolved.kind === "url") return /* @__PURE__ */ jsx("a", {
		href: resolved.href,
		...rest,
		children
	});
	const [pathname = "/", query = ""] = resolved.to.split("?");
	return /* @__PURE__ */ jsx(Link, {
		to: pathname,
		search: searchParams(query),
		...rest,
		children
	});
}
/** Query values as the router's default search parser reads them (JSON where possible). */
function searchParams(query) {
	const out = {};
	for (const [key, value] of new URLSearchParams(query)) try {
		out[key] = JSON.parse(value);
	} catch {
		out[key] = value;
	}
	return out;
}
// src/shell/breadcrumbs.tsx
const TitleContext = createContext({
	title: null,
	setTitle: () => void 0
});
function PageTitleProvider({ children }) {
	const [title, setTitle] = useState(null);
	const value = useMemo(() => ({
		title,
		setTitle
	}), [title]);
	return /* @__PURE__ */ jsx(TitleContext.Provider, {
		value,
		children
	});
}
/**
* The current page's own name (an audit's area and date, a template's title): replaces the last crumb and
* becomes the document title while the page is shown.
*/
function usePageCrumb(title) {
	const { setTitle } = useContext(TitleContext);
	useEffect(() => {
		if (!title) return;
		setTitle(title);
		return () => setTitle(null);
	}, [title, setTitle]);
}
/** The pattern with the current parameters, written with their public identifiers where known. */
const fill = (pattern, params) => pattern.replace(/\$([A-Za-z0-9_]+)/g, (_, name) => {
	const value = params[name] ?? "";
	return encodeURIComponent(publicIdOf(value) ?? value);
});
/** The trail of the deepest matched route that declares one, with the page's own title applied. */
function useRouteCrumbs() {
	const t = useT();
	const matches = useMatches();
	const { title } = useContext(TitleContext);
	const leaf = [...matches].reverse().find((m) => m.staticData?.crumbs?.length);
	const params = matches[matches.length - 1]?.params ?? {};
	const specs = leaf?.staticData.crumbs ?? [];
	return specs.map((spec, index) => {
		const last = index === specs.length - 1;
		return {
			label: last && title ? title : t(spec.label),
			href: !last && spec.to ? fill(spec.to, params) : void 0
		};
	});
}
/**
* Header breadcrumbs: where this page sits in the app. Earlier steps are links; the current page is plain
* text marked `aria-current`. Phones show the last two steps.
*/
function Breadcrumbs({ items }) {
	const t = useT();
	useEffect(() => {
		const current = items[items.length - 1]?.label;
		document.title = current ? `${current} · HLT Cloud` : "HLT Cloud";
	}, [items]);
	if (!items.length) return null;
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": t("shell.breadcrumbs"),
		className: "min-w-0 flex-1",
		children: /* @__PURE__ */ jsx(Breadcrumbs$1, {
			separator: "/",
			separatorMargin: 6,
			classNames: {
				root: "hlt-breadcrumbs flex-nowrap",
				separator: "text-muted-foreground"
			},
			children: items.map((item, index) => {
				const last = index === items.length - 1;
				const hiddenOnPhone = index < items.length - 2;
				const className = `min-w-0 truncate text-sm ${hiddenOnPhone ? "hidden sm:inline" : ""}`;
				return item.href && !last ? /* @__PURE__ */ jsx(Anchor, {
					component: Link,
					to: item.href,
					c: "dimmed",
					underline: "hover",
					className: `min-h-11 min-w-11 items-center text-sm ${hiddenOnPhone ? "hidden sm:inline-flex" : "inline-flex"}`,
					"data-phone-hidden": hiddenOnPhone || void 0,
					children: /* @__PURE__ */ jsx("span", {
						className: "min-w-0 truncate",
						children: item.label
					})
				}, `${index}-${item.label}`) : /* @__PURE__ */ jsx(Text, {
					component: "span",
					"aria-current": last ? "page" : void 0,
					fw: last ? 600 : 400,
					c: last ? void 0 : "dimmed",
					className,
					"data-phone-hidden": hiddenOnPhone || void 0,
					children: item.label
				}, `${index}-${item.label}`);
			})
		})
	});
}
// src/shell/impersonation.tsx
/** Set by a frame that already runs `ReturnAtImpersonationEnd`, so nested error and not-found views skip it. */
const EndWatched = createContext(false);
function ImpersonationEndScope({ children }) {
	return /* @__PURE__ */ jsx(EndWatched.Provider, {
		value: true,
		children
	});
}
/**
* Leave an impersonation: the API restores the operator's own session cookie, every cached
* response of the member is dropped and the browser loads the operator console afresh (also from the 5S
* origin). Without a restorable operator session the operator signs in again.
*/
function useReturnToOperator() {
	const client = useQueryClient();
	const t = useT();
	const toast = useToast();
	const [busy, setBusy] = useState(false);
	return {
		leave: useCallback(async () => {
			setBusy(true);
			try {
				const result = await call("endImpersonation", { input: {} });
				client.clear();
				clearUserScopedState();
				const { origins } = kitConfig();
				window.location.assign(appUrl(origins, result.restored && result.organizationId ? appLinks.operatorOrganization(result.organizationId) : appLinks.signIn()));
			} catch {
				setBusy(false);
				toast(t("shell.impersonationReturnFailed"), "danger");
			}
		}, [
			client,
			t,
			toast
		]),
		busy
	};
}
/** "Return to my account" as a button, for the views that stop at an impersonation (operator console, account security). */
function ReturnToAccountButton({ size = "sm" }) {
	const t = useT();
	const { leave, busy } = useReturnToOperator();
	return /* @__PURE__ */ jsx(Button, {
		size,
		variant: "filled",
		color: "dark",
		loading: busy,
		onClick: () => void leave(),
		children: t("shell.impersonationReturn")
	});
}
/**
* Returns the operator by itself when an impersonation reaches its end. Renders nothing: every `DashboardFrame`
* and standalone error or not-found view mounts it once, and nested views inside a frame are skipped.
*/
function ReturnAtImpersonationEnd() {
	const watched = useContext(EndWatched);
	const impersonation = useSession().data?.impersonation ?? null;
	const { leave } = useReturnToOperator();
	const [now, setNow] = useState(() => Date.now());
	const left = useRef(false);
	useEffect(() => {
		if (watched || !impersonation) return;
		const timer = window.setInterval(() => setNow(Date.now()), 15e3);
		return () => window.clearInterval(timer);
	}, [watched, impersonation]);
	const expiresAt = impersonation ? new Date(impersonation.expiresAt).getTime() : 0;
	useEffect(() => {
		if (watched || !impersonation || left.current || now < expiresAt) return;
		left.current = true;
		leave();
	}, [
		watched,
		impersonation,
		now,
		expiresAt,
		leave
	]);
	return null;
}
// src/shell/notification-refresh.ts
/**
* When notifications are read: when an app opens, when the person moves to another page, and when the bell's
* panel opens. Never on a timer and never because the window regained focus. One request returns the latest
* notifications, from which the unread badge is counted; a refresh within ten seconds of the last successful
* one is skipped.
*/
const notificationRefreshMs = 1e4;
/** Latest notifications per request: the panel shows the first few, the badge counts the unread ones. */
const notificationLimit = 20;
/** Query options: fetched on mount, then only through `createNotificationRefresh`. */
const notificationQueryOptions = {
	staleTime: notificationRefreshMs,
	refetchInterval: false,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	retry: false
};
/**
* Refresh trigger for page changes and panel opens: refetches unless a fetch is running or the last successful
* one is younger than `notificationRefreshMs`. Returns whether it started a request.
*/
function createNotificationRefresh(state, now = Date.now) {
	return () => {
		if (state.isFetching()) return false;
		if (now() - state.lastSuccessAt() < 1e4) return false;
		state.refetch();
		return true;
	};
}
// src/shell/use-notifications.ts
/**
* The person's latest notifications and the unread ones among them, shared by every app shell (one query
* key, so the bell and the platform home make one request). Read when the app opens and on every page
* change, never periodically; `refresh` is for opening the panel (see `notification-refresh.ts`).
*/
function useNotifications() {
	const query = useApiQuery("listNotifications", { input: { limit: 20 } }, notificationQueryOptions);
	const latest = useRef(query);
	useEffect(() => {
		latest.current = query;
	});
	const refresh = useMemo(() => createNotificationRefresh({
		lastSuccessAt: () => latest.current.dataUpdatedAt,
		isFetching: () => latest.current.isFetching,
		refetch: async () => await latest.current.refetch()
	}), []);
	const page = useRouterState({ select: (s) => s.location.href });
	useEffect(() => {
		refresh();
	}, [page, refresh]);
	const items = query.data?.items ?? [];
	return {
		query,
		items,
		unread: items.filter((n) => !n.read),
		refresh
	};
}
// src/shell/use-app-navigate.ts
/** Navigate to a place in any HLT app: client-side inside this app, a full page load into another. */
function useAppNavigate() {
	const navigate = useNavigate();
	return useCallback(async (link) => {
		const resolved = resolveLink(link);
		if (!resolved) return;
		if (resolved.kind === "url") window.location.assign(resolved.href);
		else await navigate({ to: resolved.to });
	}, [navigate]);
}
// src/shell/notification-popover.tsx
const notificationKindIcon = {
	finding: /* @__PURE__ */ jsx(ClipboardCheck, {
		"aria-hidden": true,
		className: "size-4"
	}),
	share: /* @__PURE__ */ jsx(Share2, {
		"aria-hidden": true,
		className: "size-4"
	}),
	export: /* @__PURE__ */ jsx(FileDown, {
		"aria-hidden": true,
		className: "size-4"
	}),
	templateUpdate: /* @__PURE__ */ jsx(ListChecks, {
		"aria-hidden": true,
		className: "size-4"
	}),
	allowance: /* @__PURE__ */ jsx(Gauge, {
		"aria-hidden": true,
		className: "size-4"
	}),
	invitation: /* @__PURE__ */ jsx(Mail, {
		"aria-hidden": true,
		className: "size-4"
	}),
	service: /* @__PURE__ */ jsx(Megaphone, {
		"aria-hidden": true,
		className: "size-4"
	})
};
const previewSize = 6;
/**
* Header bell with a compact overview of the latest notifications. "Show all" opens the complete list on the
* HLT Cloud home page rather than a separate page.
*/
function NotificationPopover() {
	const t = useT();
	const { i18n } = useTranslation();
	const client = useQueryClient();
	const navigate = useAppNavigate();
	const [opened, setOpened] = useState(false);
	const notifications = useNotifications();
	const latest = notifications.query;
	const count = notifications.unread.length;
	const items = notifications.items.slice(0, previewSize);
	const refresh = async () => await client.invalidateQueries({ predicate: (q) => q.queryKey[0] === "api" && q.queryKey[3] === "listNotifications" });
	const open = (n) => {
		setOpened(false);
		if (!n.read) call("readNotification", {
			params: { notificationId: n.id },
			input: {}
		}).then(refresh).catch(() => void 0);
		if (resolveLink(n.target)) navigate(n.target);
	};
	const markAll = async () => {
		const ids = notifications.unread.map((n) => n.id);
		await Promise.all(ids.map(async (id) => await call("readNotification", {
			params: { notificationId: id },
			input: {}
		}))).catch(() => void 0);
		await refresh();
	};
	const label = count ? t("shell.notificationsUnread", { count }) : t("shell.notifications");
	return /* @__PURE__ */ jsxs(Popover, {
		opened,
		onChange: setOpened,
		position: "bottom-end",
		width: 360,
		shadow: "md",
		withinPortal: true,
		children: [/* @__PURE__ */ jsx(Indicator, {
			disabled: !count,
			label: count > 9 ? "9+" : count,
			size: 16,
			offset: 7,
			color: "red",
			classNames: { indicator: "hlt-count-indicator" },
			children: /* @__PURE__ */ jsx(Popover.Target, { children: /* @__PURE__ */ jsx(ActionIcon, {
				variant: "subtle",
				color: "gray",
				size: "lg",
				"aria-label": label,
				title: t("shell.notifications"),
				onClick: () => {
					if (!opened) notifications.refresh();
					setOpened((o) => !o);
				},
				children: /* @__PURE__ */ jsx(Bell, {
					"aria-hidden": true,
					className: "size-5"
				})
			}) })
		}), /* @__PURE__ */ jsxs(Popover.Dropdown, {
			p: 0,
			"aria-label": t("shell.notifications"),
			role: "dialog",
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between gap-2 border-b border-border px-4 py-3",
					children: [/* @__PURE__ */ jsx("p", {
						className: "font-semibold",
						children: t("shell.notifications")
					}), count ? /* @__PURE__ */ jsx(UnstyledButton, {
						onClick: () => void markAll(),
						className: "rounded-[4px] px-2 py-1 text-sm font-medium text-primary-text hover:bg-muted transition",
						children: t("notifications.markAllRead")
					}) : null]
				}),
				latest.isPending ? /* @__PURE__ */ jsx("div", {
					role: "status",
					"aria-label": t("common.loading"),
					className: "flex flex-col gap-3 px-4 py-4",
					children: [
						0,
						1,
						2
					].map((row) => /* @__PURE__ */ jsxs("div", {
						className: "flex items-start gap-3",
						children: [/* @__PURE__ */ jsx(Skeleton, { className: "size-8 shrink-0 rounded-full" }), /* @__PURE__ */ jsxs("div", {
							className: "flex flex-1 flex-col gap-1.5",
							children: [/* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-4/5" }), /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-1/3" })]
						})]
					}, row))
				}) : items.length === 0 ? /* @__PURE__ */ jsx("p", {
					className: "px-4 py-6 text-sm text-muted-foreground",
					children: t("account.notificationsEmpty")
				}) : /* @__PURE__ */ jsx(ScrollArea.Autosize, {
					mah: "min(24rem, 60vh)",
					children: /* @__PURE__ */ jsx("ul", {
						className: "flex flex-col py-1",
						children: items.map((n) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(UnstyledButton, {
							onClick: () => open(n),
							className: "hlt-notification-row flex w-full gap-3 px-4 py-2.5 text-left",
							children: [
								/* @__PURE__ */ jsx("span", {
									className: cn("mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full", n.read ? "bg-muted text-muted-foreground" : "bg-accent text-primary-text"),
									children: notificationKindIcon[n.kind] ?? /* @__PURE__ */ jsx(Bell, {
										"aria-hidden": true,
										className: "size-4"
									})
								}),
								/* @__PURE__ */ jsxs("span", {
									className: "min-w-0 flex-1",
									children: [/* @__PURE__ */ jsx("span", {
										className: cn("line-clamp-2 text-sm break-words", !n.read && "font-semibold"),
										children: n.title
									}), /* @__PURE__ */ jsx("time", {
										dateTime: n.createdAt,
										title: formatDateTime(n.createdAt, i18n.language),
										className: "text-xs text-muted-foreground",
										children: formatRelative(n.createdAt, i18n.language)
									})]
								}),
								!n.read ? /* @__PURE__ */ jsx("span", {
									className: "mt-2 size-2 shrink-0 rounded-full bg-primary",
									children: /* @__PURE__ */ jsx("span", {
										className: "sr-only",
										children: t("account.unread")
									})
								}) : null
							]
						}) }, n.id))
					})
				}),
				/* @__PURE__ */ jsx("div", {
					className: "border-t border-border p-2",
					children: /* @__PURE__ */ jsx(AppAnchor, {
						link: appLinks.homeNotifications(),
						onClick: () => setOpened(false),
						className: "flex min-h-10 items-center justify-center rounded-[4px] text-sm font-medium text-primary-text hover:bg-muted transition",
						children: t("shell.allNotifications")
					})
				})
			]
		})]
	});
}
// src/shell/route-progress.tsx
/**
* Delay before the bar appears, so fast page changes stay calm instead of flickering. The sidebar marks the
* clicked destination at once; the bar follows only for anything slower.
*/
const showAfterMs = 80;
/**
* A thin bar along the top of the frame header while the router loads the next page (its code or route
* data) or a language switch fetches content in the new language. Decorative: the page's own loading state carries the accessible label.
*/
function RouteProgress() {
	const routing = useRouterState({ select: (s) => s.isLoading || s.status === "pending" });
	const loading = useLocaleSwitching() || routing;
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		if (!loading) {
			setVisible(false);
			return;
		}
		const timer = setTimeout(() => setVisible(true), showAfterMs);
		return () => clearTimeout(timer);
	}, [loading]);
	return /* @__PURE__ */ jsx("div", {
		"aria-hidden": true,
		className: "hlt-route-progress",
		"data-visible": visible || void 0
	});
}
// src/shell/logo.tsx
/**
* The HLT Cloud mark (original artwork, described in DESIGN.md): three rising bars crossed by a rising leaf.
* Built to read at 16–25 px (favicons, sidebars). Its two greens are fixed mark colors (deep `#0E6B38` tile,
* light `#41E28A` leaf) shared with the favicons and app icons, so the mark looks the same in both themes.
*/
function LogoMark({ className = "size-8" }) {
	return /* @__PURE__ */ jsxs("svg", {
		viewBox: "0 0 32 32",
		"aria-hidden": true,
		className: `shrink-0 ${className}`,
		children: [
			/* @__PURE__ */ jsx("rect", {
				width: "32",
				height: "32",
				rx: "8",
				fill: "#0E6B38"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "6",
				y: "16.5",
				width: "4.5",
				height: "9",
				rx: "2.25",
				fill: "#fff"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "13.75",
				y: "12",
				width: "4.5",
				height: "13.5",
				rx: "2.25",
				fill: "#fff"
			}),
			/* @__PURE__ */ jsx("rect", {
				x: "21.5",
				y: "6.5",
				width: "4.5",
				height: "19",
				rx: "2.25",
				fill: "#fff"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M8.25 19.4C10.6 13.4 15.6 9.9 23.75 8.75 22.6 15.9 16.8 20.4 8.25 19.4Z",
				fill: "#41E28A"
			})
		]
	});
}
const labelSizes = {
	base: "text-base",
	lg: "text-lg"
};
/**
* The mark with the product name. Every app shows `HLT Cloud` (tools name themselves in their navigation);
* `labelSize` sets the name's size, for example `base` in the site footer. The name never wraps.
*/
function Logo({ className = "h-7", label = "HLT Cloud", labelSize = "lg" }) {
	return /* @__PURE__ */ jsxs("span", {
		className: `inline-flex items-center gap-2 font-semibold tracking-tight ${className}`,
		children: [/* @__PURE__ */ jsx(LogoMark, { className: "h-full w-auto" }), label ? /* @__PURE__ */ jsx("span", {
			className: `whitespace-nowrap ${labelSizes[labelSize]}`,
			children: label
		}) : null]
	});
}
// src/shell/sidebar.tsx
const pathOf = (item) => {
	if (item.to) return item.to;
	if (!item.link) return null;
	const resolved = resolveLink(item.link);
	return resolved?.kind === "route" ? resolved.to : null;
};
function isActive(item, pathname) {
	const path = pathOf(item);
	return path !== null && (item.exact ? pathname === path || pathname === `${path}/` : pathname === path || pathname.startsWith(`${path.replace(/\/$/, "")}/`)) || (item.children ?? []).some((child) => isActive(child, pathname));
}
function SidebarEntry({ item, pathname, nested = false, onNavigate, accent }) {
	const Icon = item.icon;
	const active = isActive(item, pathname);
	const hue = item.accent ?? accent;
	const common = {
		label: item.label,
		leftSection: /* @__PURE__ */ jsx(Icon, {
			"aria-hidden": true,
			className: nested ? "size-4" : "size-5"
		}),
		rightSection: item.badge,
		active,
		"aria-current": active && !item.children ? "page" : void 0,
		className: nested ? "hlt-side-link hlt-side-link-nested" : "hlt-side-link",
		style: hue ? { "--side-accent": hue } : void 0,
		"data-accent": hue ? true : void 0
	};
	if (item.children?.length) return /* @__PURE__ */ jsx(SidebarParent, {
		item,
		common,
		active,
		pathname,
		onNavigate,
		children: item.children.map((child) => /* @__PURE__ */ jsx(SidebarEntry, {
			item: child,
			pathname,
			nested: true,
			onNavigate,
			accent: hue
		}, child.key))
	});
	if (item.to) return /* @__PURE__ */ jsx(NavLink, {
		...common,
		component: Link,
		to: item.to,
		activeOptions: {
			exact: item.exact ?? false,
			includeSearch: false
		},
		activeProps: { className: "" },
		onClick: (event) => {
			if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
			event.preventDefault();
			onNavigate(item.to ?? "");
		}
	});
	if (item.link) return /* @__PURE__ */ jsx(NavLink, {
		...common,
		component: AppAnchor,
		link: item.link
	});
	return null;
}
/**
* An expandable entry. With its own page (`to`, the analytics overview) a click opens that page and shows the
* sub-destinations, so no child repeats the parent; a click on the open entry while its page is shown folds it.
* Without a page it only folds.
*/
function SidebarParent({ item, common, active, pathname, onNavigate, children }) {
	const [opened, setOpened] = useState(active);
	useEffect(() => {
		if (active) setOpened(true);
	}, [active]);
	const path = item.to?.replace(/\/$/, "") ?? null;
	const onPage = path !== null && (pathname === path || pathname === `${path}/`);
	const activate = () => {
		if (path === null || onPage) return setOpened((open) => !open);
		setOpened(true);
		onNavigate(path);
	};
	return /* @__PURE__ */ jsx(NavLink, {
		...common,
		component: "button",
		type: "button",
		opened,
		onClick: activate,
		onKeyDown: (event) => {
			if (event.code === "Space") activate();
		},
		"aria-current": onPage ? "page" : void 0,
		childrenOffset: 18,
		rightSection: /* @__PURE__ */ jsx(ChevronRight, {
			"aria-hidden": true,
			className: "hlt-side-chevron size-4"
		}),
		children
	});
}
/**
* The current tool's navigation, in labelled groups. A clicked destination shows as current at once: the marker
* is painted first and the page change starts in the next frame, because rendering the next page would
* otherwise hold the marker back. The page's code and data were already requested when the link was pointed
* at or touched.
*/
function SidebarNav({ groups, label }) {
	const router = useRouter();
	const committed = useRouterState({ select: (s) => s.location.pathname });
	const settled = useRouterState({ select: (s) => s.status === "idle" && !s.isLoading });
	const [pending, setPending] = useState(null);
	const navigateTo = (path) => {
		setPending(path);
		requestAnimationFrame(() => setTimeout(() => void router.navigate({ to: path }), 0));
	};
	useEffect(() => {
		if (settled) setPending(null);
	}, [settled]);
	const pathname = pending ?? committed;
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": label,
		className: "flex flex-col gap-4",
		children: groups.filter((group) => group.items.length).map((group) => /* @__PURE__ */ jsxs("div", {
			role: group.label ? "group" : void 0,
			"aria-labelledby": group.label ? `side-${group.key}` : void 0,
			className: "flex flex-col gap-0.5",
			children: [group.label ? /* @__PURE__ */ jsx("p", {
				id: `side-${group.key}`,
				className: "px-3 pb-1 text-xs font-medium text-muted-foreground",
				children: group.label
			}) : null, group.items.map((item) => /* @__PURE__ */ jsx(SidebarEntry, {
				item,
				pathname,
				onNavigate: navigateTo
			}, item.key))]
		}, group.key))
	});
}
/**
* Classes and CSS variables of the organization's sidebar theme, per color scheme: the
* tinted sidebar background, hover and active surfaces, the current-destination marker and the brand name.
* Applied to the sidebar element itself; status colors are never touched.
*/
function whiteLabelStyle(branding) {
	const style = {};
	const classes = [];
	for (const theme of ["light", "dark"]) {
		const accent = branding?.[theme];
		if (!accent) continue;
		classes.push(`hlt-wl-${theme}`);
		style[`--wl-${theme}-indicator`] = accent.indicator;
		style[`--wl-${theme}-subtle`] = accent.subtle;
		style[`--wl-${theme}-text`] = accent.text;
		style[`--wl-${theme}-surface`] = accent.surface;
		style[`--wl-${theme}-hover`] = accent.hover;
	}
	return {
		className: classes.join(" "),
		style
	};
}
/**
* Sidebar head: the HLT mark (or the organization's own logo and name) with the open tool's name below.
* Links to the tool's home; the visible names are the link's accessible name.
*/
function SidebarBrand({ product, home, branding, tool }) {
	return /* @__PURE__ */ jsxs(AppAnchor, {
		link: home,
		className: "hlt-side-brand flex min-h-12 min-w-0 items-center gap-2.5 rounded-[6px] px-2 py-1.5",
		"data-tool": tool,
		style: tool ? {
			"--brand-tool-solid": `var(--tool-${tool}-solid)`,
			"--brand-tool-tile": `var(--tool-${tool}-tile-bg)`
		} : void 0,
		children: [branding?.logoUrl && branding.darkLogoUrl && branding.darkLogoUrl !== branding.logoUrl ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("img", {
			src: branding.logoUrl,
			alt: "",
			className: "hlt-wl-logo-light size-8 shrink-0 object-contain"
		}), /* @__PURE__ */ jsx("img", {
			src: branding.darkLogoUrl,
			alt: "",
			className: "hlt-wl-logo-dark size-8 shrink-0 object-contain"
		})] }) : branding?.logoUrl || branding?.darkLogoUrl ? /* @__PURE__ */ jsx("img", {
			src: branding.logoUrl ?? branding.darkLogoUrl ?? "",
			alt: "",
			className: "size-8 shrink-0 object-contain"
		}) : /* @__PURE__ */ jsx(LogoMark, { className: "size-8" }), /* @__PURE__ */ jsxs("span", {
			className: "flex min-w-0 flex-col leading-tight",
			children: [/* @__PURE__ */ jsx("span", {
				className: "hlt-side-brand-name truncate font-semibold",
				children: branding?.name ?? "HLT Cloud"
			}), product ? /* @__PURE__ */ jsxs("span", {
				className: "flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground",
				children: [tool ? /* @__PURE__ */ jsx("span", {
					"aria-hidden": true,
					className: "hlt-side-tool-marker"
				}) : null, /* @__PURE__ */ jsx("span", {
					className: "truncate",
					children: product
				})]
			}) : null]
		})]
	});
}
// src/shell/user-menu.tsx
function initialsOf(name) {
	return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}
/**
* Account menu for every header. Tools and the operator console are reached from the sidebar, not from here.
*/
function UserMenu() {
	const t = useT();
	const { i18n } = useTranslation();
	const session = useSession();
	const signOut = useSignOut();
	const { leave } = useReturnToOperator();
	const { switchLocale, pending } = useSwitchLocale();
	const impersonating = Boolean(session.data?.impersonation);
	const name = session.data?.name ?? "";
	const initials = initialsOf(name);
	return /* @__PURE__ */ jsxs(Menu, {
		position: "bottom-end",
		width: 260,
		withinPortal: true,
		children: [/* @__PURE__ */ jsx(Menu.Target, { children: /* @__PURE__ */ jsx(ActionIcon, {
			variant: "subtle",
			color: "gray",
			size: "lg",
			radius: "xl",
			"aria-label": name ? t("shell.userMenuNamed", { name }) : t("shell.userMenu"),
			children: session.data ? /* @__PURE__ */ jsx(Avatar, {
				name,
				src: session.data.avatarUrl,
				size: 30,
				children: /* @__PURE__ */ jsx("span", {
					"aria-hidden": true,
					"data-initials": initials,
					className: "after:content-[attr(data-initials)]"
				})
			}) : /* @__PURE__ */ jsx(CircleUser, {
				"aria-hidden": true,
				className: "size-5"
			})
		}) }), /* @__PURE__ */ jsxs(Menu.Dropdown, { children: [
			session.data ? /* @__PURE__ */ jsxs("div", {
				className: "px-3 py-2",
				children: [/* @__PURE__ */ jsx("p", {
					className: "truncate text-sm font-semibold",
					children: name
				}), /* @__PURE__ */ jsx("p", {
					className: "truncate text-xs text-muted-foreground",
					children: session.data.email
				})]
			}) : null,
			/* @__PURE__ */ jsx(Menu.Divider, {}),
			/* @__PURE__ */ jsx(Menu.Item, {
				component: AppAnchor,
				link: appLinks.account(),
				leftSection: /* @__PURE__ */ jsx(Settings, {
					"aria-hidden": true,
					className: "size-4"
				}),
				children: t("shell.accountSettings")
			}),
			/* @__PURE__ */ jsxs(Menu.Sub, {
				position: "left-start",
				offset: 4,
				children: [/* @__PURE__ */ jsx(Menu.Sub.Target, { children: /* @__PURE__ */ jsxs(Menu.Sub.Item, {
					leftSection: /* @__PURE__ */ jsx(Languages, {
						"aria-hidden": true,
						className: "size-4"
					}),
					children: [
						t("common.language"),
						": ",
						localeNames[locales.find((l) => l === i18n.language) ?? "en"]
					]
				}) }), /* @__PURE__ */ jsx(Menu.Sub.Dropdown, { children: locales.map((locale) => /* @__PURE__ */ jsx(Menu.Item, {
					lang: locale,
					closeMenuOnClick: false,
					disabled: pending !== null,
					onClick: () => switchLocale(locale),
					rightSection: locale === pending ? /* @__PURE__ */ jsx(Loader, { size: 14 }) : locale === i18n.language ? /* @__PURE__ */ jsx(Check, {
						"aria-hidden": true,
						className: "size-4"
					}) : null,
					"aria-current": locale === i18n.language ? "true" : void 0,
					children: localeNames[locale]
				}, locale)) })]
			}),
			/* @__PURE__ */ jsx(Menu.Divider, {}),
			impersonating ? /* @__PURE__ */ jsx(Menu.Item, {
				onClick: () => void leave(),
				leftSection: /* @__PURE__ */ jsx(Undo2, {
					"aria-hidden": true,
					className: "size-4"
				}),
				children: t("shell.impersonationReturn")
			}) : /* @__PURE__ */ jsx(Menu.Item, {
				onClick: () => void signOut(),
				leftSection: /* @__PURE__ */ jsx(LogOut, {
					"aria-hidden": true,
					className: "size-4"
				}),
				children: t("shell.signOut")
			})
		] })]
	});
}
// src/shell/workspace-selector.tsx
/**
* The one place to switch workspaces: a compact control at the bottom of the sidebar. Lists only what the API
* returns for the signed-in account, so the client never decides which workspaces a person may enter.
*/
function WorkspaceSelector({ current, onSelect }) {
	const t = useT();
	const scopes = useApiQuery("listScopes", { input: { limit: 100 } }, { staleTime: 6e4 });
	const items = scopes.data?.items ?? [];
	const selected = items.find((s) => s.organizationId === current?.organizationId && s.workspaceId === current?.workspaceId) ?? null;
	const organizations = [...new Set(items.map((s) => s.organizationId))];
	if (scopes.isSuccess && items.length === 0) return null;
	return /* @__PURE__ */ jsxs(Menu, {
		position: "top",
		offset: 6,
		width: "target",
		withinPortal: true,
		trapFocus: true,
		children: [/* @__PURE__ */ jsx(Menu.Target, { children: /* @__PURE__ */ jsxs(UnstyledButton, {
			className: "hlt-workspace-selector flex w-full min-w-0 items-center gap-2.5 rounded-[6px] px-2 py-2 text-left",
			children: [
				/* @__PURE__ */ jsx(Avatar, {
					size: 32,
					radius: "sm",
					color: "gray",
					variant: "light",
					name: selected?.organizationName,
					children: /* @__PURE__ */ jsx("span", {
						"aria-hidden": true,
						"data-initials": initialsOf(selected?.organizationName ?? "?"),
						className: "text-xs after:content-[attr(data-initials)]"
					})
				}),
				/* @__PURE__ */ jsxs("span", {
					className: "flex min-w-0 flex-1 flex-col leading-tight",
					children: [
						/* @__PURE__ */ jsx("span", {
							className: "truncate text-sm font-medium",
							children: selected?.workspaceName ?? t("shell.chooseWorkspace")
						}),
						selected ? /* @__PURE__ */ jsx("span", {
							className: "truncate text-xs text-muted-foreground",
							children: selected.organizationName
						}) : null,
						selected ? /* @__PURE__ */ jsx("span", {
							className: "sr-only",
							children: t("shell.switchWorkspace")
						}) : null
					]
				}),
				/* @__PURE__ */ jsx(ChevronsUpDown, {
					"aria-hidden": true,
					className: "size-4 shrink-0 text-muted-foreground"
				})
			]
		}) }), /* @__PURE__ */ jsx(Menu.Dropdown, { children: /* @__PURE__ */ jsx("div", {
			className: "max-h-[min(24rem,60vh)] overflow-y-auto",
			children: organizations.map((organizationId) => {
				const group = items.filter((s) => s.organizationId === organizationId);
				return /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(Menu.Label, { children: group[0]?.organizationName }), group.map((scope) => {
					const active = scope.organizationId === current?.organizationId && scope.workspaceId === current.workspaceId;
					return /* @__PURE__ */ jsx(Menu.Item, {
						onClick: () => onSelect(scope),
						"aria-current": active ? "true" : void 0,
						rightSection: active ? /* @__PURE__ */ jsx(Check, {
							"aria-hidden": true,
							className: "size-4"
						}) : null,
						children: scope.workspaceName
					}, scope.workspaceId);
				})] }, organizationId);
			})
		}) })]
	});
}
// src/shell/dashboard-frame.tsx
const collapsedKey = "hlt.sidebarCollapsed";
function readCollapsed() {
	try {
		return localStorage.getItem(collapsedKey) === "true";
	} catch {
		return false;
	}
}
function writeCollapsed(value) {
	try {
		localStorage.setItem(collapsedKey, String(value));
	} catch {}
}
const FocusViewContext = createContext({
	focusView: false,
	setFocusView: () => void 0
});
/**
* Focus view of the open page: the frame hides its header and sidebar so the page can use the whole
* browser window (not the browser's fullscreen). It ends on Escape and on every route change.
*/
function useFocusView() {
	return useContext(FocusViewContext);
}
/**
* The one dashboard layout of every signed-in HLT area: a left sidebar with the brand, the open
* tool's navigation, the route back to HLT Cloud and the workspace selector at the bottom; the content on
* the right under a header with the sidebar toggle, breadcrumbs, notifications, theme and account menu.
* The sidebar collapses fully on wide screens (remembered per app) and becomes a drawer below 768 px.
*/
function DashboardFrame(props) {
	return /* @__PURE__ */ jsx(PageTitleProvider, { children: /* @__PURE__ */ jsx(Frame, { ...props }) });
}
function Frame({ product, tool, home, navigation, cloudLink = true, workspace, branding, rootCrumbs = [], focused = false, banner, tone = "default", children }) {
	const t = useT();
	useAccountLocaleSync();
	useIdentityChangeReload();
	const switchingLocale = useLocaleSwitching();
	const wide = useMediaQuery("(min-width: 48em)", true);
	const [collapsed, setCollapsed] = useState(() => focused || readCollapsed());
	const [drawer, setDrawer] = useState(false);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const crumbs = [...rootCrumbs, ...useRouteCrumbs()];
	const [focusView, setFocusView] = useState(false);
	const focusViewState = useMemo(() => ({
		focusView,
		setFocusView
	}), [focusView]);
	useEffect(() => {
		setDrawer(false);
		setFocusView(false);
	}, [pathname]);
	useEffect(() => {
		if (!focusView) return;
		const onKey = (event) => {
			if (event.key === "Escape" && !event.defaultPrevented) setFocusView(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [focusView]);
	useEffect(() => setCollapsed(focused || readCollapsed()), [focused]);
	const open = wide ? !collapsed : drawer;
	const toggle = () => {
		if (!wide) return setDrawer((d) => !d);
		setCollapsed((c) => {
			if (!focused) writeCollapsed(!c);
			return !c;
		});
	};
	const brand = tone === "operator" ? null : branding;
	const whiteLabel = whiteLabelStyle(brand);
	const sidebar = /* @__PURE__ */ jsxs("div", {
		className: "flex h-full min-h-0 flex-col gap-2",
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "px-1 pt-1",
				children: /* @__PURE__ */ jsx(SidebarBrand, {
					product,
					home,
					branding: brand,
					tool
				})
			}),
			/* @__PURE__ */ jsx(ScrollArea, {
				type: "hover",
				className: "min-h-0 flex-1",
				offsetScrollbars: "present",
				children: /* @__PURE__ */ jsx("div", {
					className: "px-1 py-2",
					children: navigation
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-col gap-1 border-t border-border px-1 pt-2 pb-1",
				children: [cloudLink ? /* @__PURE__ */ jsx(NavLink, {
					component: AppAnchor,
					link: appLinks.platformHome(),
					label: t("shell.backToCloud"),
					leftSection: /* @__PURE__ */ jsx(LayoutGrid, {
						"aria-hidden": true,
						className: "size-5"
					}),
					className: "hlt-side-link"
				}) : null, workspace ? /* @__PURE__ */ jsx(WorkspaceSelector, {
					current: workspace.current,
					onSelect: workspace.onSelect
				}) : null]
			})
		]
	});
	return /* @__PURE__ */ jsxs(AppShell, {
		layout: "alt",
		header: { height: 56 },
		navbar: {
			width: 272,
			breakpoint: "md",
			collapsed: {
				mobile: true,
				desktop: collapsed
			}
		},
		disabled: focusView,
		padding: focusView ? 0 : {
			base: "md",
			md: "xl"
		},
		transitionDuration: 180,
		transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
		className: tone === "operator" ? "hlt-frame hlt-frame-operator" : "hlt-frame",
		children: [
			/* @__PURE__ */ jsxs(AppShell.Header, {
				component: "header",
				className: "hlt-frame-header",
				children: [/* @__PURE__ */ jsx(RouteProgress, {}), /* @__PURE__ */ jsxs("div", {
					className: "flex h-full items-center gap-2 px-3 md:px-4",
					children: [
						/* @__PURE__ */ jsx(ActionIcon, {
							variant: "subtle",
							color: "gray",
							size: "lg",
							onClick: toggle,
							"aria-label": open ? t("shell.hideSidebar") : t("shell.showSidebar"),
							title: open ? t("shell.hideSidebar") : t("shell.showSidebar"),
							"aria-expanded": open,
							children: open ? /* @__PURE__ */ jsx(PanelLeftClose, {
								"aria-hidden": true,
								className: "size-5"
							}) : /* @__PURE__ */ jsx(PanelLeftOpen, {
								"aria-hidden": true,
								className: "size-5"
							})
						}),
						/* @__PURE__ */ jsx(Breadcrumbs, { items: crumbs }),
						/* @__PURE__ */ jsxs("div", {
							className: "ml-auto flex shrink-0 items-center gap-1",
							children: [
								/* @__PURE__ */ jsx(NotificationPopover, {}),
								/* @__PURE__ */ jsx(ThemeToggle, {}),
								/* @__PURE__ */ jsx(UserMenu, {})
							]
						})
					]
				})]
			}),
			/* @__PURE__ */ jsx(AppShell.Navbar, {
				component: "aside",
				p: "sm",
				className: `hlt-frame-sidebar ${whiteLabel.className}`,
				style: whiteLabel.style,
				"aria-label": t("shell.sidebar"),
				inert: wide && collapsed,
				children: wide ? sidebar : null
			}),
			!wide ? /* @__PURE__ */ jsx(Drawer, {
				opened: drawer,
				onClose: () => setDrawer(false),
				position: "left",
				size: "min(18rem, 86vw)",
				withCloseButton: false,
				padding: "sm",
				classNames: {
					content: `hlt-frame-sidebar hlt-frame-drawer ${whiteLabel.className}`,
					body: "h-full"
				},
				styles: { content: whiteLabel.style },
				attributes: { content: { "aria-label": t("shell.sidebar") } },
				children: sidebar
			}) : null,
			/* @__PURE__ */ jsxs(AppShell.Main, {
				"aria-busy": switchingLocale || void 0,
				className: switchingLocale ? "hlt-locale-switching" : void 0,
				children: [
					/* @__PURE__ */ jsx(ReturnAtImpersonationEnd, {}),
					banner,
					/* @__PURE__ */ jsx(ImpersonationEndScope, { children: /* @__PURE__ */ jsx("main", {
						id: "main",
						className: "hlt-frame-main",
						children: /* @__PURE__ */ jsx(FocusViewContext.Provider, {
							value: focusViewState,
							children
						})
					}) })
				]
			})
		]
	});
}
// src/shell/error-summary.tsx
/** Error summary shown on submit; focus moves here so screen readers announce it. */
function ErrorSummary({ errors, labels }) {
	const t = useT();
	const ref = useRef(null);
	useEffect(() => {
		if (errors.length) ref.current?.focus();
	}, [errors]);
	if (!errors.length) return null;
	return /* @__PURE__ */ jsx(Alert, {
		ref,
		tabIndex: -1,
		role: "alert",
		color: "red",
		"data-tone": "danger",
		icon: /* @__PURE__ */ jsx(AlertTriangle, {
			"aria-hidden": true,
			className: "size-4"
		}),
		title: t("errors.fieldSummary", { count: errors.length }),
		children: /* @__PURE__ */ jsx("ul", {
			className: "mt-1 list-disc pl-5",
			children: errors.slice(0, 8).map((e) => /* @__PURE__ */ jsxs("li", { children: [labels?.[e.path] ? `${labels[e.path]}: ` : "", e.message] }, e.path))
		})
	});
}
// src/shell/short-urls.ts
/** Platform path segments followed by an organization or workspace identifier. */
const platformIdentifierAfter = /* @__PURE__ */ new Set([
	"o",
	"organizations",
	"workspaces"
]);
/**
* Swap identifiers at the platform's identifier positions: a UUID for its public identifier when one was seen
* (`toPublic`) or a known public identifier back to its UUID. Unknown values stay as they are.
*/
function mapPlatformIds(path, toPublic) {
	const segments = path.split("/");
	for (let i = 1; i < segments.length; i++) {
		const previous = segments[i - 1];
		const segment = segments[i];
		if (!previous || !segment || !platformIdentifierAfter.has(previous)) continue;
		const mapped = toPublic ? isUuid(segment) ? publicIdOf(segment) : null : isPublicId(segment) ? idOfPublicId(segment) : null;
		if (mapped) segments[i] = mapped;
	}
	return segments.join("/");
}
/**
* Router rewrite for short addresses. Paths use public identifiers where a resource has one; a UUID left in an
* address is shown in its 22-character notation. Incoming addresses in every older form are normalized before
* matching, so links from earlier emails or bookmarks still open: 22-character identifiers are expanded to UUIDs
* and, with `dropOrganization` (the 5S tool), `/o/<org>/w/<ws>/…` becomes `/w/<ws>/…`. With `publicIds` (the
* platform, whose routes keep UUID parameters) organization and workspace UUIDs are shown as their public
* identifiers once the API has returned them (`rememberPublicIds`), and a known public identifier opens with
* its UUID.
*/
function createShortUrlRewrite(options = {}) {
	const normalize = (path) => {
		const expanded = expandPath(options.dropOrganization ? dropOrganizationSegment(path) : path);
		return options.publicIds ? mapPlatformIds(expanded, false) : expanded;
	};
	const present = (path) => shortenPath(options.publicIds ? mapPlatformIds(path, true) : path);
	return {
		input: ({ url }) => {
			const path = normalize(url.pathname);
			if (path === url.pathname) return void 0;
			url.pathname = path;
			return url;
		},
		output: ({ url }) => {
			const path = present(url.pathname);
			if (path === url.pathname) return void 0;
			url.pathname = path;
			return url;
		}
	};
}
/**
* The platform's rewrite: organizations and workspaces by their 11-character public identifiers in the address
* bar, any other UUID in its 22-character form, UUIDs inside the app.
*/
const shortIdRewrite = createShortUrlRewrite({ publicIds: true });
/**
* An in-app address as the router expects it: short identifiers in the path expanded to UUIDs. Use it
* when a path comes from the address bar or a `next` parameter and is navigated to inside the app.
*/
function routerHref(href) {
	return mapPath(href, expandPath);
}
/** An in-app address as the address bar shows it: UUIDs in the path written in their short form. */
function addressHref(href) {
	return mapPath(href, shortenPath);
}
function mapPath(href, map) {
	const end = href.search(/[?#]/);
	return `${map(end === -1 ? href : href.slice(0, end))}${end === -1 ? "" : href.slice(end)}`;
}
// src/shell/guard.ts
/**
* A session confirmed this recently is trusted for navigation without a request. The guard itself does not
* revalidate it; the frame's `useSession` observer refetches it once it is a minute old (on mount and window
* focus), and the server checks every request regardless.
*/
const trustedForMs = 6e4;
/** The last confirmed session, so a tool reopened without a connection can show what is kept on the device. */
const snapshotKey = "hlt.sessionSnapshot";
function readSnapshot() {
	try {
		const raw = localStorage.getItem(snapshotKey);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}
function writeSnapshot(session) {
	try {
		localStorage.setItem(snapshotKey, JSON.stringify(session));
	} catch {}
}
/**
* Leave for the platform's sign-in or MFA step with a return target. Inside the platform this is a
* router redirect; a tool sends the browser to the platform with its absolute URL to come back to.
*/
async function leaveFor(link, href) {
	const resolved = resolveLink(link);
	if (resolved?.kind === "route") throw redirect({
		to: resolved.to,
		search: { next: addressHref(href) }
	});
	const back = `${window.location.origin}${addressHref(href)}`;
	window.location.assign(`${resolved?.href ?? "/"}?next=${encodeURIComponent(back)}`);
	return await new Promise(() => void 0);
}
const signedOut = (error) => isApiError(error) && (error.code === "unauthenticated" || error.code === "mfa_required");
/** Who the cached responses belong to: the account and, while impersonating, the impersonation. */
const identityOf = (session) => session ? `${session.userId}:${session.impersonation?.id ?? ""}` : null;
async function fetchSession(queryClient, previous) {
	const session = await call("getSession");
	if (previous && identityOf(previous) !== identityOf(session)) {
		queryClient.clear();
		clearUserScopedState();
	}
	queryClient.setQueryData(apiKey("getSession"), session);
	if (!session.mfaRequired && !session.impersonation) writeSnapshot(session);
	return session;
}
/**
* An impersonation that ran out answers as signed out; the browser may still return to the operator's own
* session (within the grace period), which is where it goes instead of the sign-in page.
*/
async function returnFromExpiredImpersonation() {
	try {
		const result = await call("endImpersonation", { input: {} });
		if (!result.restored || !result.organizationId) return false;
		clearUserScopedState();
		const target = resolveLink(appLinks.operatorOrganization(result.organizationId));
		window.location.assign(target?.kind === "url" ? target.href : target?.to ?? "/");
		return true;
	} catch {
		return false;
	}
}
/**
* Resolve the session before a protected page renders; redirect to sign-in or the MFA step.
*
* Navigation inside an app must not wait for the network: a session confirmed within the last minute is used
* directly (the frame's `useSession` observer refetches it when it is older), and the server still checks
* every request. Without a connection the last confirmed session of this device lets the app open what it
* keeps locally; every request still fails closed on the server, and sign-out removes the snapshot.
*/
async function requireSession(queryClient, href, allowEnrollment = false) {
	const state = queryClient.getQueryState(apiKey("getSession"));
	let session;
	if (state?.data && state.status === "success" && Date.now() - state.dataUpdatedAt < trustedForMs) session = state.data;
	else try {
		session = await fetchSession(queryClient, state?.data);
	} catch (error) {
		if (signedOut(error)) {
			const wasImpersonating = Boolean(state?.data?.impersonation);
			queryClient.clear();
			if (wasImpersonating && await returnFromExpiredImpersonation()) return await new Promise(() => void 0);
			return await leaveFor(appLinks.signIn(), href);
		}
		const offline = isApiError(error) && error.code === "network";
		const fallback = state?.data ?? (offline ? readSnapshot() : null);
		if (!fallback) throw error;
		queryClient.setQueryData(apiKey("getSession"), fallback);
		session = fallback;
	}
	if (!session.mfaRequired && !session.impersonation) await claimDevice(session.userId);
	if (session.mfaRequired) await leaveFor(appLinks.mfa(), href);
	if (session.mfaEnrollmentRequired && !allowEnrollment) await leaveFor(appLinks.mfaSetup(), href);
}
// src/shell/locale-switch.tsx
/** Interface language switch for pages outside the dashboard frame; the choice applies to every HLT app in this browser. */
function LocaleSwitch({ compact = false }) {
	const { i18n } = useTranslation();
	const t = useT();
	const { switchLocale, pending } = useSwitchLocale();
	return /* @__PURE__ */ jsxs("label", {
		className: "inline-flex items-center gap-2 text-sm",
		children: [/* @__PURE__ */ jsx("span", {
			className: compact ? "sr-only" : "text-muted-foreground",
			children: t("common.language")
		}), /* @__PURE__ */ jsx(FieldSelect, {
			"aria-label": t("common.language"),
			className: "min-h-10 w-auto py-1 text-sm",
			value: pending ?? i18n.language,
			disabled: pending !== null,
			onChange: (e) => {
				const locale = locales.find((value) => value === e.target.value);
				if (locale) switchLocale(locale);
			},
			children: locales.map((l) => /* @__PURE__ */ jsx("option", {
				value: l,
				children: localeNames[l]
			}, l))
		})]
	});
}
// src/shell/standalone-header.tsx
/**
* Header of pages outside the dashboard frame (error and not-found views, the signed-out hop, the share
* viewer): the mark, optional context, then the theme and language switches every HLT page offers. `width`
* matches the page's content column.
*/
function StandaloneHeader({ children, width = "max-w-[1080px]" }) {
	return /* @__PURE__ */ jsx("header", {
		className: "border-b border-border bg-background",
		children: /* @__PURE__ */ jsxs("div", {
			className: `mx-auto flex w-full items-center gap-3 px-4 py-3 ${width}`,
			children: [
				/* @__PURE__ */ jsx(Logo, { className: "h-6" }),
				children,
				/* @__PURE__ */ jsxs("div", {
					className: "ml-auto flex items-center gap-1",
					children: [/* @__PURE__ */ jsx(ThemeToggle, {}), /* @__PURE__ */ jsx(LocaleSwitch, { compact: true })]
				})
			]
		})
	});
}
// src/shell/route-states.tsx
/**
* Outside a frame (unknown routes, failed route loads) the mark and the theme and language switches are shown
* here, and an impersonation still returns at its end.
*/
function NotFoundView() {
	const t = useT();
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(ReturnAtImpersonationEnd, {}),
		/* @__PURE__ */ jsx(StandaloneHeader, {}),
		/* @__PURE__ */ jsx("main", {
			id: "main",
			className: "mx-auto max-w-2xl px-4 py-16",
			children: /* @__PURE__ */ jsx(StateView, {
				kind: "notFound",
				title: t("errors.notFoundTitle"),
				action: /* @__PURE__ */ jsx(AppAnchor, {
					link: appLinks.platformHome(),
					className: "text-primary-text underline",
					children: t("errors.goHome")
				}),
				children: t("errors.notFoundBody")
			})
		})
	] });
}
function RouteErrorView({ error, reset }) {
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx(ReturnAtImpersonationEnd, {}),
		/* @__PURE__ */ jsx(StandaloneHeader, {}),
		/* @__PURE__ */ jsx("main", {
			id: "main",
			className: "mx-auto max-w-2xl px-4 py-16",
			children: /* @__PURE__ */ jsx(ApiErrorView, {
				error,
				onRetry: reset
			})
		})
	] });
}
/** Map API errors to the shared states: not found, denied, MFA enrollment, feature, read-only, offline. */
function ApiErrorView({ error, onRetry, compact = false }) {
	const t = useT();
	const apiError = isApiError(error) ? error : new ApiError(500, "internal", "Unexpected error");
	const retry = onRetry ? /* @__PURE__ */ jsx(Button$1, {
		variant: "default",
		onClick: onRetry,
		children: t("common.retry")
	}) : null;
	switch (apiError.code) {
		case "not_found": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "notFound",
			title: t("errors.notFoundTitle"),
			children: t("errors.notFoundBody")
		});
		case "forbidden": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "denied",
			title: t("errors.deniedTitle"),
			children: t("errors.deniedGeneric")
		});
		case "mfa_enrollment_required": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "denied",
			title: t("errors.mfaTitle"),
			action: /* @__PURE__ */ jsx(AppAnchor, {
				link: appLinks.mfaSetup(),
				className: "inline-flex min-h-11 items-center rounded-[4px] bg-primary px-4 font-medium text-primary-foreground",
				children: t("errors.mfaAction")
			}),
			children: t("errors.mfaBody")
		});
		case "feature_disabled": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "denied",
			title: t("errors.featureTitle"),
			children: t("errors.featureBody")
		});
		case "read_only": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "denied",
			title: t("errors.readOnlyTitle"),
			children: t("errors.readOnlyBody")
		});
		case "unauthenticated":
		case "mfa_required": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "denied",
			title: t("errors.sessionExpiredTitle"),
			action: /* @__PURE__ */ jsx(AppAnchor, {
				link: appLinks.signIn(),
				className: "text-primary-text underline",
				children: t("errors.signInAgain")
			}),
			children: t("errors.sessionExpiredBody")
		});
		case "network":
		case "unavailable": return connectionProblem(apiError) === "offline" ? /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "offline",
			title: t("errors.offlineTitle"),
			action: retry,
			children: t("errors.offlineBody")
		}) : /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "offline",
			title: t("errors.unreachableTitle"),
			action: retry,
			children: t(connectionMessageKeys.unreachable)
		});
		case "rate_limited": return /* @__PURE__ */ jsx(StateView, {
			compact,
			kind: "error",
			title: t("errors.genericTitle"),
			action: retry,
			children: t("errors.rateLimited")
		});
		default: return /* @__PURE__ */ jsxs(StateView, {
			compact,
			kind: "error",
			title: t("errors.genericTitle"),
			action: retry,
			children: [/* @__PURE__ */ jsx("p", { children: t("errors.genericBody") }), apiError.requestId ? /* @__PURE__ */ jsx("p", {
				className: "mt-2 font-mono text-xs",
				children: t("common.requestId", { id: apiError.requestId })
			}) : null]
		});
	}
}
// src/shell/scope-hooks.ts
/** The actor's role and names for a workspace scope, from the cached scope list. */
function useWorkspaceScope(organizationId, workspaceId) {
	const scopes = useApiQuery("listScopes", { input: { limit: 100 } }, { staleTime: 6e4 });
	return {
		scope: scopes.data?.items.find((s) => s.organizationId === organizationId && s.workspaceId === workspaceId) ?? null,
		isPending: scopes.isPending,
		error: scopes.error
	};
}
/**
* The actor's role in an organization. HLT operators who are not members administer it directly
* (`viaOperator`), so the organization pages work for them without a membership.
*/
function useOrganizationRole(organizationId) {
	const session = useSession();
	const orgs = useApiQuery("listMyOrganizations", { input: { limit: 100 } }, { staleTime: 6e4 });
	const member = orgs.data?.items.find((o) => o.id === organizationId) ?? null;
	const operatorLookup = Boolean(session.data?.isOperator && !session.data.impersonation && orgs.isSuccess && !member);
	const direct = useApiQuery("getOrganization", { params: { organizationId } }, {
		enabled: operatorLookup,
		retry: false,
		staleTime: 6e4
	});
	const viaOperator = operatorLookup && direct.isSuccess;
	const organization = member ?? (viaOperator ? {
		id: organizationId,
		name: direct.data.name,
		role: "administrator"
	} : null);
	return {
		organization,
		isAdmin: organization?.role === "administrator",
		viaOperator,
		isPending: orgs.isPending || session.isPending || operatorLookup && direct.isPending
	};
}
// src/shell/sidebar-branding.ts
/**
* White-label sidebar branding of the organization in scope, ready for `DashboardFrame`'s
* `branding` prop. Null while loading, without an organization, or when the plan or the administrator has not
* enabled it; the frame then shows HLT Cloud.
*/
function useSidebarBranding(organizationId) {
	const data = useApiQuery("getSidebarBranding", { params: { organizationId: organizationId ?? "" } }, {
		enabled: Boolean(organizationId),
		staleTime: 6e4,
		retry: false
	}).data;
	if (!organizationId || !data?.enabled || !data.name) return null;
	return {
		name: data.name,
		logoUrl: data.logoMediaId ? mediaUrl(organizationId, data.logoMediaId, "thumbnail") : null,
		darkLogoUrl: data.darkLogoMediaId ? mediaUrl(organizationId, data.darkLogoMediaId, "thumbnail") : null,
		light: data.light,
		dark: data.dark
	};
}
// src/shell/signed-out-page.tsx
/**
* Hop of a sign-out that started in another app: the session is already gone, so this app clears what
* it keeps in its own origin's storage and continues to `next`. Copies of the account's data (the offline
* copy, the session snapshot) are removed at once; unsent drafts are not removed silently: the person
* removes them or signs in again to send them. A removal that fails is said so, with a retry. Content
* only: the app supplies the page frame.
*/
function SignedOutPage({ next }) {
	const t = useT();
	const signIn = appUrl(kitConfig().origins, appLinks.signIn());
	const target = safeReturn(next, signIn);
	const [unsent, setUnsent] = useState(null);
	const [failed, setFailed] = useState(false);
	const clearAndContinue = useCallback(async () => {
		setFailed(false);
		try {
			await clearDeviceState();
			window.location.replace(target);
		} catch {
			setUnsent(await pendingWorkCount());
			setFailed(true);
		}
	}, [target]);
	useEffect(() => {
		let active = true;
		(async () => {
			const copiesCleared = await clearDeviceCopies().then(() => true, () => false);
			const count = await pendingWorkCount();
			if (!active) return;
			if (count > 0 || !copiesCleared) {
				setUnsent(count);
				setFailed(!copiesCleared);
			} else clearAndContinue();
		})();
		return () => {
			active = false;
		};
	}, [clearAndContinue]);
	const home = `${window.location.origin}/`;
	if (unsent === null) return /* @__PURE__ */ jsx(SkeletonList, {
		rows: 2,
		label: t("shell.signingOut")
	});
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col gap-4",
		children: [
			/* @__PURE__ */ jsx("h1", {
				className: "text-xl font-semibold",
				children: t("shell.signedOutTitle")
			}),
			unsent > 0 ? /* @__PURE__ */ jsx("p", {
				className: "text-base text-muted-foreground",
				children: t("shell.signedOutDrafts", { count: unsent })
			}) : null,
			failed ? /* @__PURE__ */ jsx("p", {
				role: "alert",
				className: "text-sm text-destructive",
				children: t("account.deviceClearFailed")
			}) : null,
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-col gap-2 sm:flex-row",
				children: [/* @__PURE__ */ jsx(Button$1, {
					onClick: () => void clearAndContinue(),
					children: unsent > 0 ? t("shell.removeDraftsAndContinue") : t("common.retry")
				}), unsent > 0 ? /* @__PURE__ */ jsx(Button$1, {
					variant: "default",
					onClick: () => window.location.assign(`${signIn}?next=${encodeURIComponent(home)}`),
					children: t("shell.signInToSend")
				}) : null]
			})
		]
	});
}
// src/shell/skip-link.tsx
/** First focusable element of every app page: jumps past the top bar to `#main` (WCAG 2.4.1). */
function SkipLink() {
	const t = useT();
	return /* @__PURE__ */ jsx("a", {
		href: "#main",
		className: "skip-link",
		children: t("common.skipToContent")
	});
}
export { ApiErrorView, AppAnchor, Breadcrumbs, DashboardFrame, ErrorSummary, ImpersonationEndScope, LocaleSwitch, Logo, LogoMark, NotFoundView, NotificationPopover, PageTitleProvider, ReturnAtImpersonationEnd, ReturnToAccountButton, RouteErrorView, SidebarBrand, SidebarNav, SignedOutPage, SkipLink, StandaloneHeader, UserMenu, WorkspaceSelector, addressHref, createNotificationRefresh, createShortUrlRewrite, initialsOf, notificationKindIcon, notificationLimit, notificationQueryOptions, notificationRefreshMs, requireSession, routerHref, shortIdRewrite, useAppNavigate, useFocusView, useNotifications, useOrganizationRole, usePageCrumb, useReturnToOperator, useRouteCrumbs, useSidebarBranding, useWorkspaceScope, whiteLabelStyle };

//# sourceMappingURL=shell.js.map